import type { Facet, Facets } from 'codesearch-backend'
import { For, Show } from 'solid-js'
import { searchQuery, setSearchQuery } from '../store/search.ts'
import { cn } from '../utils/cn.ts'

/*
  counts come from every file that matched, not just the page on screen - the indexer
  greps the whole candidate set before it truncates, so these are honest numbers.

  clicking one appends the corresponding `field:value` to the query instead of
  keeping a separate filter state: the query box stays the single source of truth,
  and the filter is something you can then edit or delete by hand
*/
function FacetGroup(props: { title: string, field: string, values: Facet[] }) {
  const active = (value: string) => new RegExp(`(^|\\s)${props.field}:${value}(\\s|$)`).test(searchQuery())

  function toggle(value: string) {
    const token = `${props.field}:${value}`
    const current = searchQuery()
    if (active(value)) {
      setSearchQuery(current.split(/\s+/).filter(it => it !== token).join(' ').trim())
    } else {
      setSearchQuery(`${current} ${token}`.trim())
    }
  }

  return (
    <Show when={props.values.length > 1}>
      <div>
        <div class="mb-1 px-1 text-xs text-muted-foreground">{props.title}</div>
        <For each={props.values}>
          {facet => (
            <button
              type="button"
              class={cn(
                'flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-xs transition-colors hover:bg-muted/60',
                active(facet.value) && 'bg-accent/50',
              )}
              onClick={() => toggle(facet.value)}
            >
              <span class="min-w-0 flex-1 truncate font-mono">{facet.value}</span>
              <span class="shrink-0 text-muted-foreground tabular-nums">{facet.files.toLocaleString()}</span>
            </button>
          )}
        </For>
      </div>
    </Show>
  )
}

export function FacetsPanel(props: { facets: Facets }) {
  return (
    <div class="w-48 flex shrink-0 flex-col gap-3 overflow-y-auto border-r border-border p-2">
      <FacetGroup title="index" field="repo" values={props.facets.repo} />
      <FacetGroup title="language" field="lang" values={props.facets.lang} />
      <FacetGroup title="extension" field="ext" values={props.facets.ext} />
    </div>
  )
}
